import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Bell, Check, CheckCheck, Inbox, Filter,
  FileCheck, CheckCircle2, XCircle, Undo2, Megaphone, HandCoins,
  ArrowLeftRight, Store, Mail, BadgeCheck, Lock, UserCog,
  Sparkles, MessageSquare, Eye,
} from "lucide-react";
import { toast } from "sonner";

const ICON_MAP = {
  Bell, FileCheck, CheckCircle2, XCircle, Undo2, Megaphone, HandCoins,
  ArrowLeftRight, Store, Mail, BadgeCheck, Lock, UserCog,
  Sparkles, MessageSquare, Eye,
};

function fmtDate(ts) {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleString(); } catch { return ts; }
}

export default function Notifications() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | unread

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/notifications?limit=200${filter === "unread" ? "&only_unread=true" : ""}`);
      setItems(r.data.items || []);
      setUnread(r.data.unread_count || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load notifications");
    } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { reload(); }, [reload]);

  if (!user) return null;

  const markOne = async (id) => {
    setItems((arr) => arr.map((n) => n.notif_id === id ? { ...n, read: true } : n));
    setUnread((u) => Math.max(0, u - 1));
    try { await api.post(`/notifications/${id}/read`); } catch { reload(); }
  };

  const markAll = async () => {
    try {
      const r = await api.post("/notifications/read-all");
      toast.success(`Marked ${r.data.updated || 0} read`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-6 sm:py-10" data-testid="notifications-page">
      <header className="border-b border-border pb-5 sm:pb-6 mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="font-overline text-accent">Inbox</div>
          <h1 className="font-display text-3xl sm:text-4xl mt-2">Notifications</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            All your platform notifications. {unread > 0 && <span className="font-medium text-foreground">{unread} unread.</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex border border-border" role="tablist">
            <button data-testid="notif-filter-all" role="tab"
              aria-selected={filter === "all"}
              onClick={() => setFilter("all")}
              className={`px-3 h-8 text-xs uppercase tracking-wider ${filter === "all" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}>
              All
            </button>
            <button data-testid="notif-filter-unread" role="tab"
              aria-selected={filter === "unread"}
              onClick={() => setFilter("unread")}
              className={`px-3 h-8 text-xs uppercase tracking-wider border-l border-border ${filter === "unread" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}>
              Unread
            </button>
          </div>
          {unread > 0 && (
            <Button data-testid="notif-page-mark-all" size="sm" variant="outline"
              className="rounded-none uppercase text-xs tracking-wider" onClick={markAll}>
              <CheckCheck className="w-3.5 h-3.5 mr-1.5" /> Mark all read
            </Button>
          )}
        </div>
      </header>

      {loading ? (
        <div className="space-y-2" data-testid="notif-page-loading">
          {[0, 1, 2].map((i) => <div key={i} className="h-20 bg-muted/30 animate-pulse" />)}
        </div>
      ) : items.length === 0 ? (
        <div data-testid="notif-page-empty" className="border border-dashed border-border p-12 text-center">
          <Inbox className="w-10 h-10 mx-auto text-muted-foreground/40 mb-3" />
          <div className="font-medium">No {filter === "unread" ? "unread " : ""}notifications</div>
          <p className="text-sm text-muted-foreground mt-1">
            When activity happens on your account it will show up here.
          </p>
        </div>
      ) : (
        <div className="border border-border divide-y divide-border" data-testid="notif-page-list">
          {items.map((n) => {
            const Icon = ICON_MAP[n.icon] || Bell;
            return (
              <div key={n.notif_id} data-testid={`notif-row-${n.notif_id}`}
                className={`flex items-start gap-3 sm:gap-4 px-4 sm:px-5 py-4 hover:bg-muted/30 ${!n.read ? "bg-primary/5" : ""}`}>
                <div className={`w-9 h-9 flex items-center justify-center flex-shrink-0 ${!n.read ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-sm ${!n.read ? "font-semibold" : ""}`}>{n.title}</span>
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-muted text-muted-foreground">
                      {n.category}
                    </span>
                    {!n.read && (
                      <span data-testid={`notif-row-${n.notif_id}-unread`}
                        className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-red-500/10 text-red-700 dark:text-red-300">
                        unread
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-muted-foreground mt-0.5">{n.description}</div>
                  <div className="text-[11px] text-muted-foreground/70 mt-1.5 font-mono-data">{fmtDate(n.created_at)}</div>
                </div>
                <div className="flex flex-col sm:flex-row gap-1.5 flex-shrink-0">
                  {n.link && (
                    <button data-testid={`notif-open-${n.notif_id}`}
                      onClick={() => { if (!n.read) markOne(n.notif_id); navigate(n.link); }}
                      className="text-xs px-2 h-8 border border-border hover:bg-background uppercase tracking-wider">
                      Open
                    </button>
                  )}
                  {!n.read && (
                    <button data-testid={`notif-mark-${n.notif_id}`}
                      onClick={() => markOne(n.notif_id)}
                      className="text-xs px-2 h-8 border border-border hover:bg-background uppercase tracking-wider flex items-center gap-1">
                      <Check className="w-3 h-3" /> Read
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
